import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
    Alert,
    Box,
    ButtonGroup,
    Chip,
    Paper,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    DataGridPro,
    GridToolbarContainer,
    GridToolbarDensitySelector,
    GridToolbarFilterButton,
} from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import TalentClient, { TalentErrors } from '../apiClients/TalentClient';
import BusySpinner from '../components/BusySpinner';
import { useErrorDisplay } from '../components/ErrorDisplay';
import OnlyForRole from '../components/OnlyForRole';
import PageTitle from '../components/PageTitle';
import PanelSegment from '../components/PanelSegment';
import { useSearchBox } from '../components/SearchBox';
import { useMainDisplayHeight } from '../components/WindowDimensions';
import { useAccessInfo } from '../contexts/AccessTokenProvider';
import { Securables, UpsertAction } from '../Enums';
import { FormatDateTime, TalentFullName } from '../Helpers';
import { I18N } from '../I18N';
import { TalentDisplay } from '../models/dtos';
import TalentManagementPopup from '../popups/TalentManagementPopup';
import TalentUpsertPopup from '../popups/TalentUpsertPopup';
import MobileEntityCard from '../components/responsive/MobileEntityCard';
import ResponsiveCollection from '../components/responsive/ResponsiveCollection';
import useViewportMode from '../hooks/useViewportMode';
import {
    filterTalentsByStatus,
    getTalentActivityPeriodCount,
    isTalentActive,
    TalentStatusFilter,
} from './talentStatus';
import EmployeePortalAccessAction from '../components/EmployeePortalAccessAction';

const TalentManagementPage = () => {
    const [ErrorDisplay, setError] = useErrorDisplay();
    const [talents, setTalents] = useState<TalentDisplay[]>([]);
    const [statusFilter, setStatusFilter] = useState<TalentStatusFilter>('active');
    const [busy, setBusy] = useState<boolean>(false);
    const initialized = useRef<boolean>(false);
    const [SearchBox, searchFilter] = useSearchBox();
    const access = useAccessInfo();
    const { mode: viewportMode } = useViewportMode();
    const mainDisplayHeight = useMainDisplayHeight();

    const loadData = async () => {
        try {
            setBusy(true);
            const response = await new TalentClient().GetAll(true);
            setTalents([...response].sort((a, b) => a.FirstName.localeCompare(b.FirstName)));
        } catch (error) {
            setError(error, TalentErrors.GetAll);
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        if (initialized.current)
            return;

        initialized.current = true;
        loadData();
    });

    const visibleTalents = useMemo(
        () => searchFilter(filterTalentsByStatus(talents, statusFilter)),
        [searchFilter, statusFilter, talents],
    ) as TalentDisplay[];

    const formatDate = (value?: string) => value
        ? FormatDateTime(dayjs(value).toString())
        : '—';

    const renderTalentCard = (talent: TalentDisplay) => {
        const fullName = TalentFullName(
            talent,
            access.accessInfo?.selectedOrganizationSettings?.DisplayFirstNameThenLast ?? true,
        );
        const activityHistory = talent.ActivationHistory?.map(period =>
            `${formatDate(period.ActivatedDate)} – ${period.DeactivatedDate ? formatDate(period.DeactivatedDate) : 'Present'}`,
        ).join('; ') || 'No history supplied';

        const fields = [
            { label: 'Org Identifier', value: talent.OrganizationIdentifier || '—' },
            { label: 'Active Periods', value: getTalentActivityPeriodCount(talent) },
            { label: 'Last Activated', value: formatDate(talent.ActivatedDate) },
            ...(statusFilter !== 'active' ? [{
                label: 'Last Deactivated',
                value: isTalentActive(talent) ? '' : formatDate(talent.DeactivatedDate),
            }] : []),
            { label: 'Activity History', value: activityHistory, fullWidth: true },
            { label: 'Last Modified', value: formatDate(talent.LastUpdateDate), fullWidth: true },
        ];

        return <MobileEntityCard
            ariaLabel={`Teammate: ${fullName}`}
            title={fullName}
            subtitle={talent.PositionTitle || 'No position assigned'}
            status={statusFilter === 'all' ? <Chip
                size="small"
                label={isTalentActive(talent) ? 'Active' : 'Inactive'}
                color={isTalentActive(talent) ? 'success' : 'default'}
                variant={isTalentActive(talent) ? 'filled' : 'outlined'}
            /> : undefined}
            fields={fields}
            actions={
                <Stack direction="row" spacing={0.5}>
                    <OnlyForRole role={Securables.talent_modify}>
                        <TalentManagementPopup
                            mode={UpsertAction.Update}
                            talentFullName={fullName}
                            talentId={talent.Id}
                            initialTab="EditInfo"
                            icon={<EditOutlinedIcon fontSize="small" />}
                            onChange={loadData}
                            hoverContent={`Edit ${fullName}`}
                            aria-label={`Edit teammate: ${fullName}`}
                        />
                    </OnlyForRole>
                    <OnlyForRole role={Securables.organization_employee_invite_create}>
                        <EmployeePortalAccessAction talentId={talent.Id} talentName={fullName} deliveryEmail={talent.Email} />
                    </OnlyForRole>
                </Stack>
            }
        />;
    };

    const desktopGrid = (
        <div style={{ height: mainDisplayHeight - 72, width: '100%' }}>
            <DataGridPro
                density="compact"
                rows={visibleTalents.map(x => ({ ...x, id: x.Id }))}
                columns={[
                    {
                        field: 'actions',
                        headerName: '',
                        sortable: false,
                        filterable: false,
                        resizable: false,
                        width: 96,
                        align: 'center',
                        disableColumnMenu: true,
                        renderCell: ({ row }) =>
                            <ButtonGroup>
                                <OnlyForRole role={Securables.talent_modify}>
                                    <TalentManagementPopup
                                        mode={UpsertAction.Update}
                                        talentFullName={TalentFullName(
                                            row,
                                            access.accessInfo?.selectedOrganizationSettings?.DisplayFirstNameThenLast ?? true,
                                        )}
                                        talentId={row.id}
                                        initialTab="EditInfo"
                                        icon={<EditOutlinedIcon fontSize="small" />}
                                        onChange={loadData}
                                    />
                                </OnlyForRole>
                                <OnlyForRole role={Securables.organization_employee_invite_create}>
                                    <EmployeePortalAccessAction
                                        talentId={row.id}
                                        deliveryEmail={row.Email}
                                        talentName={TalentFullName(
                                            row,
                                            access.accessInfo?.selectedOrganizationSettings?.DisplayFirstNameThenLast ?? true,
                                        )}
                                    />
                                </OnlyForRole>
                            </ButtonGroup>,
                    },
                    ...(statusFilter === 'all' ? [{
                        field: 'Status',
                        headerName: 'Status',
                        width: 105,
                        valueGetter: ({ row }) => isTalentActive(row) ? 'Active' : 'Inactive',
                        renderCell: ({ row }) =>
                            <Chip
                                size="small"
                                label={isTalentActive(row) ? 'Active' : 'Inactive'}
                                color={isTalentActive(row) ? 'success' : 'default'}
                                variant={isTalentActive(row) ? 'filled' : 'outlined'}
                            />,
                    }] : []),
                    { field: 'FirstName', headerName: 'First Name', flex: 1, minWidth: 120 },
                    { field: 'LastName', headerName: 'Last Name', flex: 1, minWidth: 120 },
                    { field: 'PositionTitle', headerName: 'Title', flex: 1, minWidth: 140 },
                    { field: 'OrganizationIdentifier', headerName: 'Org Identifier', flex: 1, minWidth: 120 },
                    {
                        field: 'ActivatedDate',
                        headerName: 'Last Activated',
                        width: 165,
                        renderCell: ({ value }) => formatDate(value),
                    },
                    ...(statusFilter !== 'active' ? [{
                        field: 'DeactivatedDate',
                        headerName: 'Last Deactivated',
                        width: 165,
                        renderCell: ({ row }) => isTalentActive(row) ? '' : formatDate(row.DeactivatedDate),
                    }] : []),
                    {
                        field: 'ActivityPeriods',
                        headerName: 'Active Periods',
                        width: 125,
                        valueGetter: ({ row }) => getTalentActivityPeriodCount(row),
                        renderCell: ({ row }) => {
                            const periods = getTalentActivityPeriodCount(row);
                            const detail = row.ActivationHistory?.map(period =>
                                `${formatDate(period.ActivatedDate)} – ${period.DeactivatedDate ? formatDate(period.DeactivatedDate) : 'Present'}`,
                            ).join('\n') || 'No history supplied';
                            return <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{detail}</span>}>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <HistoryOutlinedIcon fontSize="small" color="action" />
                                    <span>{periods}</span>
                                </Stack>
                            </Tooltip>;
                        },
                    },
                    {
                        field: 'LastUpdateDate',
                        headerName: 'Last Modified',
                        width: 165,
                        renderCell: ({ value }) => formatDate(value),
                    },
                ]}
                components={{
                    Toolbar: () => <GridToolbarContainer>
                        <GridToolbarFilterButton onResize={undefined} onResizeCapture={undefined} />
                        <GridToolbarDensitySelector onResize={undefined} onResizeCapture={undefined} />
                    </GridToolbarContainer>,
                }}
            />
        </div>
    );

    return <>
        <ErrorDisplay />
        <BusySpinner component={Paper} busy={busy}>
            <PanelSegment
                titleContent={<PageTitle title={I18N.Translate('Talent Management')} />}
                toolbar={
                    <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ gap: 1 }}>
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={statusFilter}
                            aria-label="Teammate status view"
                            onChange={(_, value: TalentStatusFilter) => value && setStatusFilter(value)}>
                            <ToggleButton value="active">Active</ToggleButton>
                            <ToggleButton value="inactive">Inactive</ToggleButton>
                            <ToggleButton value="all">All</ToggleButton>
                        </ToggleButtonGroup>
                        <ButtonGroup>
                            <OnlyForRole role={Securables.talent_create}>
                                <TalentUpsertPopup mode={UpsertAction.Create} onAdd={() => loadData()} visibleLabel={I18N.Translate('Add Teammate')} aria-label={I18N.Translate('Add Teammate')} />
                            </OnlyForRole>
                            <SearchBox />
                        </ButtonGroup>
                    </Stack>
                }
                fillHeight>
                <Box>
                    <Alert severity={statusFilter === 'inactive' ? 'warning' : 'info'} sx={{ m: 2, mb: 1 }}>
                        {statusFilter === 'inactive'
                            ? 'Inactive teammates remain available for historical review. Reactivating restores them to the billable active count without deleting prior activation periods.'
                            : 'Active teammates are billable. Deactivating a teammate preserves their history, but billing continues through the current paid period.'}
                    </Alert>
                    <ResponsiveCollection
                        viewMode={viewportMode}
                        items={visibleTalents}
                        getKey={talent => talent.Id}
                        renderCard={renderTalentCard}
                        desktop={desktopGrid}
                        ariaLabel="Teammates"
                        emptyState={<Typography sx={{ p: 2 }} color="text.secondary">No teammates found</Typography>}
                    />
                </Box>
            </PanelSegment>
        </BusySpinner>
    </>;
};

export default TalentManagementPage;
